import React from 'react';
import { Copy, ShieldAlert } from 'lucide-react';
import type { IssuedApiKey } from '../../generated/api/types.gen.js';
import { Button, Callout, useToast } from '../../components/ui/index.js';

interface IssuedKeyCalloutProps {
  issued: IssuedApiKey;
  onDismiss: () => void;
}

/** Shows a freshly issued or rotated key's plaintext once, with Copy. Conflui keeps only the hash. */
export const IssuedKeyCallout: React.FC<IssuedKeyCalloutProps> = ({ issued, onDismiss }) => {
  const { notify } = useToast();

  const copy = async () => {
    await navigator.clipboard.writeText(issued.plaintext);
    notify('Key copied');
  };

  return (
    <Callout
      tone="warning"
      icon={ShieldAlert}
      title="Copy this key now. It will not be shown again."
      actions={
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      }
    >
      <p>
        Store it in your deployment's secret manager. Conflui keeps only a hash of the key, so a lost key has to be
        rotated.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded-sm border bg-bg px-3 py-2 font-mono text-xs text-heading">
          {issued.plaintext}
        </code>
        <Button size="sm" variant="secondary" onClick={copy}>
          <Copy size={14} aria-hidden="true" />
          Copy
        </Button>
      </div>
    </Callout>
  );
};
